import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Layout,
  Typography,
  Spin,
  Alert,
  Modal,
  Input,
  Button,
  Space,
  message,
  Card,
  Empty,
} from 'antd';
import { LockOutlined, EyeOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { v2AnalysisApi } from '../../../api';
import CompetitorAnalysis from './CompetitorAnalysis';

const { Content } = Layout;
const { Title, Text } = Typography;

const SharedAnalysis = () => {
  const { analysisId } = useParams();
  const [loading, setLoading] = useState(true);
  const [analysisData, setAnalysisData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [inputKey, setInputKey] = useState(Date.now()); // パスワードフィールドを強制再生成

  // 共有リサーチデータを取得
  const fetchSharedAnalysis = async (providedPassword = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await v2AnalysisApi.getSharedAnalysis(
        analysisId,
        providedPassword,
      );

      if (response.password_required) {
        setPasswordRequired(true);
        setInputKey(Date.now()); // パスワードフィールドを再生成
        setLoading(false);
        return;
      }

      if (response.analysis && response.metadata) {
        setAnalysisData(response.analysis);
        setMetadata(response.metadata);
        setPasswordRequired(false);
        setPassword('');
      } else {
        setError('リサーチデータの形式が正しくありません。');
      }
    } catch (error) {
      console.error('Failed to fetch shared analysis:', error);

      if (error.response?.status === 404) {
        setError('指定されたリサーチ結果が見つかりません。');
      } else if (error.response?.status === 403) {
        setError('このリサーチ結果は共有されていません。');
      } else {
        setError('リサーチ結果の取得に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  // パスワード認証
  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      message.error('パスワードを入力してください');
      return;
    }

    try {
      setPasswordLoading(true);
      await fetchSharedAnalysis(password);
    } catch (error) {
      message.error('パスワードが正しくありません');
    } finally {
      setPasswordLoading(false);
    }
  };

  // 初回データ取得
  useEffect(() => {
    if (analysisId) {
      fetchSharedAnalysis();
    }
  }, [analysisId]);

  // パスワード入力フォーム
  const renderPasswordForm = () => (
    <>
      <Helmet>
        <title>パスワード認証 - リサーチ結果共有 | magicss</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <Content
          style={{
            padding: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Card style={{ textAlign: 'center', minWidth: 400, maxWidth: 500 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <LockOutlined
                  style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }}
                />
                <Title level={3} style={{ margin: 0, color: '#333' }}>
                  パスワード認証
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: 14, marginTop: 8, display: 'block' }}
                >
                  {metadata?.keyword}
                  のリサーチ結果を閲覧するにはパスワードが必要です。
                </Text>
              </div>

              <div style={{ width: '100%' }}>
                {/* 動的に生成されるパスワードフィールド */}
                <Input.Password
                  key={inputKey} // 強制再レンダリング
                  placeholder="パスワードを入力してください"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onPressEnter={handlePasswordSubmit}
                  size="large"
                  style={{ marginBottom: 16 }}
                  autoComplete="one-time-code"
                  data-lpignore="true"
                  data-form-type="other"
                  name={`password-${inputKey}`} // 動的name属性
                />
                <Button
                  type="primary"
                  size="large"
                  loading={passwordLoading}
                  onClick={handlePasswordSubmit}
                  style={{ width: '100%' }}
                >
                  認証してリサーチ結果を表示
                </Button>
              </div>
            </Space>
          </Card>
        </Content>
      </Layout>
    </>
  );

  // エラー表示
  if (error) {
    return (
      <>
        <Helmet>
          <title>エラー - リサーチ結果共有 | magicss</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
          <Content
            style={{
              padding: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Card style={{ maxWidth: 500, textAlign: 'center' }}>
              <Empty description={error} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              <Button
                type="primary"
                onClick={() => (window.location.href = '/')}
                style={{ marginTop: 16 }}
              >
                ホームに戻る
              </Button>
            </Card>
          </Content>
        </Layout>
      </>
    );
  }

  // ローディング表示
  if (loading) {
    return (
      <>
        <Helmet>
          <title>読み込み中... - リサーチ結果共有 | magicss</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
          <Content
            style={{
              padding: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Card style={{ textAlign: 'center', minWidth: 300 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">リサーチ結果を読み込んでいます...</Text>
              </div>
            </Card>
          </Content>
        </Layout>
      </>
    );
  }

  // パスワード認証が必要な場合
  if (passwordRequired) {
    return renderPasswordForm();
  }

  // メイン表示（リサーチデータが取得できた場合）
  return (
    <>
      <Helmet>
        <title>{`「${metadata?.keyword}」のリサーチ結果 - magicss`}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta
          name="description"
          content={`「${metadata?.keyword}」のSEO・AIOリサーチ結果を共有しています。`}
        />
      </Helmet>

      <Layout style={{ minHeight: '100vh', background: '#fff' }}>
        {/* ヘッダー・サイドバーなし、メインコンテンツのみ */}
        <Content
          style={{
            padding: '24px',
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {/* 共有ページ用のヘッダー */}
          <div
            style={{
              marginBottom: 24,
              textAlign: 'center',
              borderBottom: '1px solid #f0f0f0',
              paddingBottom: 16,
            }}
          >
            <Space direction="vertical" size="small">
              <Title
                level={1}
                style={{ margin: 0, fontSize: 28, color: '#333' }}
              >
                「{metadata?.keyword}」のリサーチ結果
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                このリサーチ結果は{' '}
                {metadata?.created_at
                  ? new Date(metadata.created_at).toLocaleDateString('ja-JP')
                  : ''}{' '}
                に作成されました
              </Text>
            </Space>
          </div>

          {/* リサーチ結果表示（CompetitorAnalysisコンポーネントを再利用） */}
          {analysisData && (
            <CompetitorAnalysis
              sessionData={{
                keyword: metadata?.keyword || '',
                analysisId: metadata?.analysis_id || '',
                location: 'Japan',
                language: 'ja',
                step1AnalysisResult: {
                  analysis_id: metadata?.analysis_id || '',
                },
              }}
              analysisData={analysisData}
              initialAnalysisState={{
                status: 'completed',
                progress: 100,
                currentStep: null,
              }}
              isSharedView={true} // 共有ビューフラグ
              enableProgressUI={false}
              showNavigation={false} // ナビゲーションボタンを非表示
              showShareSettings={false} // 共有設定を非表示
            />
          )}
        </Content>
      </Layout>
    </>
  );
};

export default SharedAnalysis;
